import { requireById } from './dom'
import { showToast } from './admin-ui'

interface Photo {
  src: string
  alt: string
  /** Caption takes — "↻ Another take" cycles through them. */
  takes: string[]
  /** Set when the AI culled the shot instead of offering it. */
  skipped?: string
}

const PHOTOS: Photo[] = [
  {
    src: '/img/hero-breach.jpg',
    alt: 'Humpback breaching beside the boat',
    takes: [
      'Three humpbacks off the heads this morning — and the 9 am crew had front-row seats. Tomorrow has 14 left. 🐋',
      'This is why we set the alarm. Full breach, first light, 9 am crew losing it.',
    ],
  },
  {
    src: '/img/feed-06.jpg',
    alt: 'Breach right beside cheering guests',
    takes: [
      'A boat-length away. Nobody’s coffee survived, no regrets. Seats left for tomorrow’s 9 am. ☕🐋',
      'When the whale decides it’s YOUR tour. This morning, one boat-length off the rail.',
    ],
  },
  {
    src: '/img/feed-02.jpg',
    alt: 'Guest phone catching a breach mid-frame',
    takes: [
      'Caught mid-breach on a guest’s phone — every camera on board got the shot. Yours could be next.',
      'The photo their mates won’t believe. Straight off a guest’s phone from this morning’s run.',
    ],
  },
  {
    src: '/img/feed-01.jpg',
    alt: 'Fluke wave in front of the bow',
    takes: [
      'Tail salute from this morning’s escort — she stayed with the boat for twenty minutes. 👋',
      'The politest goodbye on the Sapphire Coast. Twenty minutes alongside, then this.',
    ],
  },
  {
    src: '/img/feed-03.jpg',
    alt: 'Guest with a long lens, spout on the horizon',
    takes: [
      '27 trips, 27 whales — and Geoff finally got his frame. Bring the big lens, we’ll find the subject.',
      'Geoff’s been chasing this shot for three trips. This morning the ocean said yes.',
    ],
  },
  {
    src: '/img/feed-04.jpg',
    alt: 'Misty fluke shot from the small boat',
    takes: [],
    skipped: 'Set aside — spray on the lens',
  },
]

/**
 * The social studio: this morning's photos on the left, the branded post
 * Toura built from the selected one on the right. Posting and scheduling
 * are the only two buttons an operator should need at the dock.
 */
export function initSocial(): void {
  const grid = requireById('socgrid')
  const image = requireById<HTMLImageElement>('spimg')
  const caption = requireById('spcap')
  const retake = requireById<HTMLButtonElement>('spretake')
  const postNow = requireById<HTMLButtonElement>('sppost')
  const schedule = requireById<HTMLButtonElement>('spsched')
  const result = requireById('spresult')

  let selected = 0
  let take = 0
  /** Which photos have already gone out, so the buttons reset per photo. */
  const posted = new Set<number>()

  const photo = (): Photo => PHOTOS[selected]!

  function renderGrid(): void {
    grid.replaceChildren(
      ...PHOTOS.map((p, i) => {
        const cell = document.createElement('button')
        cell.type = 'button'
        cell.className = `soc-cell${i === selected ? ' on' : ''}${p.skipped ? ' skip' : ''}`
        cell.disabled = Boolean(p.skipped)
        cell.innerHTML =
          `<img src="${p.src}" alt="${p.alt}" loading="lazy">` +
          (p.skipped ? `<span class="soc-skip">${p.skipped}</span>` : '') +
          (posted.has(i) ? '<span class="soc-done">Posted ✓</span>' : '')
        if (!p.skipped) {
          cell.addEventListener('click', () => {
            selected = i
            take = 0
            renderGrid()
            renderPost()
          })
        }
        return cell
      }),
    )
  }

  function renderPost(): void {
    image.src = photo().src
    image.alt = `${photo().alt} — with Seabreeze branding applied`
    caption.textContent = photo().takes[take] ?? ''

    const isPosted = posted.has(selected)
    postNow.disabled = isPosted
    schedule.disabled = isPosted
    postNow.textContent = isPosted ? 'Posted ✓' : 'Post now'
    schedule.textContent = 'Schedule 4:10 pm'
    result.textContent = isPosted
      ? 'Live on Instagram + Facebook · caption links to tomorrow’s 9 am'
      : ''
  }

  retake.addEventListener('click', () => {
    const takes = photo().takes
    take = (take + 1) % takes.length
    caption.textContent = takes[take] ?? ''
  })

  postNow.addEventListener('click', () => {
    posted.add(selected)
    renderGrid()
    renderPost()
    showToast('Posted to Instagram + Facebook — the booking link points at tomorrow’s 9 am.')
    // The payoff arrives the way it does in real life: a moment later.
    window.setTimeout(() => {
      if (posted.has(selected)) result.textContent = 'Live on Instagram + Facebook · ↗ first click-through 38 seconds later'
    }, 2600)
  })

  schedule.addEventListener('click', () => {
    schedule.textContent = 'Scheduled 4:10 pm ✓'
    schedule.disabled = true
    postNow.disabled = true
    result.textContent = 'Queued for 4:10 pm — your followers’ peak hour'
    showToast('Scheduled for 4:10 pm — when your followers actually scroll.')
  })

  renderGrid()
  renderPost()
}
